import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';

import { Modal, useT } from '@repo/common';
import VisaInfoFullPage from './VisaInfoFullPage';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/16px/icon_16px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const PLACEHOLDER_TEXT = 'Tap to add your current visa';

const EMPTY_ENTRY = {
  visaType: null,
  visaTypeText: null,
  expiryDate: null,
};

const formatVisaLabel = (v) =>
  v?.visaType ? `${v.visaType} ${v.visaTypeText ?? ''}`.trim() : '';

const formatExpiry = (v) => {
  if (!v?.expiryDate) return '';
  const d =
    v.expiryDate instanceof Date ? v.expiryDate : new Date(v.expiryDate);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'dd MMM yyyy');
};

const PlaceholderCard = ({ onClick }) => {
  const t = useT();

  return (
    <div className='flex items-center'>
      <span className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray04 py-[14px] w-[20px]'>
        <DragAndDropIcon />
      </span>
      <button
        type='button'
        onClick={onClick}
        className='flex-1 text-left rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 p-3 typo-b14Strong text-text-placeholder'
      >
        {t(PLACEHOLDER_TEXT, '탭하여 현재 비자를 추가해 주세요')}
      </button>
    </div>
  );
};

const MobileVisaInfoForm = ({ visa, setResumeData }) => {
  const t = useT();
  const list = Array.isArray(visa) ? visa : [];

  const [editor, setEditor] = useState({ open: false, mode: 'add', id: null });
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const commit = (next) => setResumeData((prev) => ({ ...prev, visa: next }));

  const openAdd = () => setEditor({ open: true, mode: 'add', id: null });
  const openEdit = (id) => setEditor({ open: true, mode: 'edit', id });
  const closeEditor = () => setEditor({ open: false, mode: 'add', id: null });

  const addRow = () => commit([...list, { id: uuidv4(), ...EMPTY_ENTRY }]);

  const handleSave = (data) => {
    if (editor.mode === 'add') {
      commit([...list, { id: uuidv4(), ...data }]);
    } else {
      commit(list.map((v) => (v.id === editor.id ? { ...v, ...data } : v)));
    }
    closeEditor();
  };

  const handleConfirmDelete = () => {
    commit(list.filter((v) => v.id !== deleteTargetId));
    setDeleteTargetId(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(list);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    commit(items);
  };

  const editingEntry =
    editor.mode === 'edit' ? list.find((v) => v.id === editor.id) : null;

  return (
    <div className='flex flex-col gap-3 w-full'>
      {list.length === 0 ? (
        <PlaceholderCard onClick={openAdd} />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='mobile-visa' direction='vertical'>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex flex-col gap-3 w-full'
              >
                {list.map((v, index) => {
                  const label = formatVisaLabel(v);
                  const expiry = formatExpiry(v);

                  return (
                    <Draggable
                      key={v.id}
                      draggableId={String(v.id)}
                      index={index}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className='flex items-center gap-2 w-full'
                        >
                          {/* Drag handle */}
                          <span
                            {...prov.dragHandleProps}
                            className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray03'
                          >
                            <DragAndDropIcon width={20} height={20} />
                          </span>

                          {/* 요약 버튼 */}
                          <button
                            type='button'
                            onClick={() => openEdit(v.id)}
                            className='flex-1 min-w-0 w-full flex items-start gap-1 rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 px-4 py-3'
                          >
                            <div className='flex flex-col items-start gap-1 w-full'>
                              <p
                                className={`truncate typo-b14Strong ${
                                  label
                                    ? 'text-text-primary'
                                    : 'text-text-placeholder'
                                }`}
                              >
                                {label ||
                                  t(
                                    PLACEHOLDER_TEXT,
                                    '탭하여 현재 비자를 추가해 주세요',
                                  )}
                              </p>
                              {expiry && (
                                <p className='truncate typo-b12 text-text-secondary'>
                                  {expiry}
                                </p>
                              )}
                            </div>
                            {/* Delete */}
                            <button
                              type='button'
                              onClick={() => setDeleteTargetId(v.id)}
                              className='shrink-0 text-grayscale-gray04 w-6 min-h-[26px] flex justify-end items-start'
                              aria-label={t('Delete', '삭제')}
                            >
                              <DeleteIcon width={20} height={20} />
                            </button>
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <button
        type='button'
        onClick={addRow}
        className='flex items-center justify-center h-12 rounded-[8px] bg-grayscale-gray01 text-grayscale-gray05 transition-colors active:bg-grayscale-gray02'
        aria-label={t('Add visa', '비자 추가')}
      >
        <PlusIcon width={20} height={20} />
      </button>

      {editor.open && (
        <VisaInfoFullPage
          initialData={editingEntry}
          onClose={closeEditor}
          onSave={handleSave}
        />
      )}

      {deleteTargetId && (
        <Modal
          isOpen
          onClickLeftBtn={() => setDeleteTargetId(null)}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this visa entry?', '이 비자 항목을 삭제할까요?')}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 취소할 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default MobileVisaInfoForm;
